import { useAdminBillingQuery, useDownloadBillingInvoice } from '#/api/api.portal';
import { AdminBillingTransaction } from '#/api/types';
import mixins from '#/app/styles';
import { BusinessUserSwitcher } from '#/components/BusinessUserSwitcher/BusinessUserSwitcher';
import Button from '#/components/Button/Button';
import Heading from '#/components/Heading/Heading';
import Loader from '#/components/Loader/Loader';
import PageWrapper from '#/components/PageWrapper/PageWrapper';
import { formatPrice } from '#/utils';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Text, View } from 'react-native';
import styles from './AdminBilling.module.scss';
import { tableStyles } from './AdminBilling.styles';

const Table = ({ data }: { data: AdminBillingTransaction[] }) => {
  const { mutateAsync: downloadInvoice, isPending } = useDownloadBillingInvoice();
  const [invoiceIdInDownload, setInvoiceIdInDownload] = useState<number | null>(null);

  if (!data) {
    return null;
  }

  const uppercaseFirstLetter = (s = '') => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const handleDownload = (transactionId: number) => {
    setInvoiceIdInDownload(transactionId);
    downloadInvoice(transactionId).finally(() => setInvoiceIdInDownload(null));
  };

  const mapTransactionToRow = (t: AdminBillingTransaction) => {
    return (
      <View style={tableStyles.transactionRow} key={t.transactionId}>
        <Text style={tableStyles.columnDate}>
          {dayjs(t.createdAt).format('DD.MM.YYYY HH:mm:ss')}
        </Text>
        <Text style={tableStyles.columnUser}>{t.user?.login}</Text>
        <Text style={tableStyles.columnDescription}>{t.description}</Text>
        <Text style={tableStyles.columnMethod}>{uppercaseFirstLetter(t.paymentMethod)}</Text>
        <Text style={tableStyles.columnValue}>{formatPrice(t.value, { removeSign: true })}</Text>
        <Text style={tableStyles.columnInvoice}>
          <Button
            disabled={isPending}
            loading={invoiceIdInDownload === t.transactionId && isPending}
            secondary
            onPress={() => handleDownload(t.transactionId)}
          >
            Download
          </Button>
        </Text>
      </View>
    );
  };

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.table}>
        <View style={[tableStyles.transactionRow, tableStyles.transactionHeadingRow]}>
          <Text style={tableStyles.columnDate}>Date</Text>
          <Text style={tableStyles.columnUser}>User</Text>
          <Text style={tableStyles.columnDescription}>Description</Text>
          <Text style={tableStyles.columnMethod}>Payment method</Text>
          <Text style={tableStyles.columnValue}>Amount</Text>
          <Text style={tableStyles.columnInvoice}>Invoice</Text>
        </View>
        {data.map(mapTransactionToRow)}
      </div>
    </div>
  );
};

export const AdminBillingScreen = () => {
  const [page, setPage] = useState(1);
  const paymentTransactions = useAdminBillingQuery({ page });

  return (
    <PageWrapper>
      <BusinessUserSwitcher />
      <Heading style={mixins().styles.margin.topI8}>Billing history</Heading>
      {paymentTransactions?.isPending ? <Loader size="large" /> : null}
      {paymentTransactions.data && (
        <>
          <Table data={paymentTransactions.data.transactions} />
          <div className={styles.pagination}>
            <Button disabled={page === 1} onPress={() => setPage(prev => Math.max(1, prev - 1))}>
              Previous page
            </Button>
            <Button
              disabled={paymentTransactions.data?.pagination?.hasMore === false}
              onPress={() => setPage(prev => prev + 1)}
            >
              Next page
            </Button>
          </div>
        </>
      )}
    </PageWrapper>
  );
};
